import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'

import { apiClient } from './client'
import type {
  MessagingCostSummary,
  NotificationCategory,
  NotificationChannel,
  NotificationPage,
  NotificationStatus,
  NotificationSummary,
  ProviderBalance,
} from './notifications-types'

export interface OutboxQuery {
  status?: NotificationStatus
  channel?: NotificationChannel
  category?: NotificationCategory
  branchId?: string
  from?: string
  to?: string
  page?: number
  pageSize?: number
}

/**
 * The outbox: every message the institution has queued, and what became of it.
 *
 * Short stale time and no background refetch by default. This is the screen somebody opens when a
 * customer says they did not get their alert, and a figure from two minutes ago answers a different
 * question from the one being asked.
 */
export const outboxQueryOptions = (query: OutboxQuery = {}) =>
  queryOptions({
    queryKey: ['notifications', 'outbox', query] as const,
    queryFn: async () => {
      const response = await apiClient.get<NotificationPage>('/notifications/outbox', {
        params: {
          status: query.status,
          channel: query.channel,
          category: query.category,
          branchId: query.branchId,
          from: query.from,
          to: query.to,
          page: query.page ?? 1,
          pageSize: query.pageSize ?? 25,
        },
      })

      const data = response.data
      return {
        items: Array.isArray(data?.items) ? data.items : [],
        page: Number(data?.page ?? 1),
        pageSize: Number(data?.pageSize ?? 25),
        totalCount: Number(data?.totalCount ?? 0),
      } satisfies NotificationPage
    },
    staleTime: 10 * 1000,
  })

/**
 * What the gateway says is left in the account.
 *
 * `available: false` means the gateway could not be asked, which is not the same as a balance of
 * zero and must not be rendered as one — an institution that thinks it has no credit stops sending
 * alerts it could have sent.
 */
export const providerBalanceQueryOptions = () =>
  queryOptions({
    queryKey: ['notifications', 'provider-balance'] as const,
    queryFn: async () => (await apiClient.get<ProviderBalance>('/notifications/provider/balance')).data,
    staleTime: 5 * 60 * 1000,
  })

export const messagingCostsQueryOptions = (from: string, to: string) =>
  queryOptions({
    queryKey: ['notifications', 'costs', from, to] as const,
    queryFn: async () =>
      (await apiClient.get<MessagingCostSummary>('/notifications/costs', { params: { from, to } })).data,
    staleTime: 60 * 1000,
  })

/** Puts a failed message back in the queue. The server decides whether it is eligible. */
export function useRetryNotification() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (notificationId: string) =>
      (await apiClient.post<NotificationSummary>(`/notifications/outbox/${notificationId}/retry`)).data,
    onSuccess: () => client.invalidateQueries({ queryKey: ['notifications', 'outbox'] }),
  })
}

/**
 * Stops trying, permanently, with a reason.
 *
 * Abandoning is a decision rather than a cleanup: the message is not deleted, it stays in the
 * outbox marked abandoned with the name of whoever gave up on it, because "the customer was never
 * told" is a fact somebody may need months later.
 */
export function useAbandonNotification() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async ({ notificationId, reason }: { notificationId: string; reason: string }) =>
      (
        await apiClient.post<NotificationSummary>(
          `/notifications/outbox/${notificationId}/abandon`,
          { reason },
        )
      ).data,
    onSuccess: () => client.invalidateQueries({ queryKey: ['notifications', 'outbox'] }),
  })
}
